import { useState, useEffect, useContext } from "react";
import {
  Newspaper,
  TrendingUp,
  Users,
  Target,
  Bell,
  MousePointer,
  CalendarIcon,
  Activity,
  BadgePlus,
  BadgeMinus,
} from "lucide-react";
import StatCard from "../../components/StatCard";
import { format } from "date-fns";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import SidebarList from "../../components/Sidebar";
import Layout from "../../components/Layout/Layout";
import { Loader2 } from "lucide-react";
import { AuthContext } from "../../hoc/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { Calendar } from "../../components/ui/calendar";

const baseUrl = process.env.REACT_APP_NOTIFICATION_BASE_URL;
const DOMAIN_NAME = "marathijagran.com";

const COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const buildUrl = (domain, path) => `${baseUrl}${path}/${domain}`;

export default function Dashboard() {
  const [analyticsData, setAnalyticsData] = useState({
    totalSubscribers: 0,
    totalNotification: 0,
    totalTopics: 0,
    totalScheduledNotification: 0,
  });

  const { selectedDomain } = useContext(AuthContext);
  const [deviceData, setDeviceData] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [dailyRecords, setDailyRecords] = useState([]); // Unified daily data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const domainUrl = selectedDomain?.domain_url || DOMAIN_NAME;

  // New unified daily record URL
  const DAILY_RECORD_URL = domainUrl
    ? `${baseUrl}analytics/notification/daily-record/${domainUrl}${
        startDate && endDate
          ? `?from=${format(startDate, "yyyy-MM-dd")}&to=${format(
              endDate,
              "yyyy-MM-dd"
            )}`
          : ""
      }`
    : null;

  const DASHBOARD_ANALYTICS_URL = domainUrl
    ? `${baseUrl}analytics/dashboard/record/${domainUrl}${
        startDate && endDate
          ? `?from=${format(startDate, "yyyy-MM-dd")}&to=${format(
              endDate,
              "yyyy-MM-dd"
            )}`
          : ""
      }`
    : null;

  const DEVICE_COUNTS_URL = domainUrl
    ? buildUrl(domainUrl, "analytics/device-counts")
    : null;

  const PREFERENCE_COUNTS_URL = domainUrl
    ? buildUrl(domainUrl, "analytics/preference-counts")
    : null;

  const stats = [
    {
      name: "Total Subscribers",
      value: analyticsData.totalSubscribers.toLocaleString(),
      change: "",
      changeType: "positive",
      icon: Users,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      name: "Notifications Sent",
      value: analyticsData.totalNotification.toLocaleString(),
      change: "",
      changeType: "positive",
      icon: Bell,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      name: "CTR",
      value: analyticsData?.totalCtr?.toLocaleString() || "0",
      change: "",
      changeType: "positive",
      icon: Target,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      name: "Scheduled Notification",
      value: analyticsData.totalScheduledNotification.toLocaleString(),
      change: "",
      changeType: "positive",
      icon: MousePointer,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      name: "Opt-in",
      value: analyticsData?.totalOptIn?.toLocaleString() || "0",
      change: "",
      changeType: "positive",
      icon: BadgePlus,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      name: "Opt out",
      value: analyticsData?.totalOptOut?.toLocaleString() || "0",
      change: "",
      changeType: "positive",
      icon: BadgeMinus,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      name: "Delivery Rate",
      value: analyticsData?.deliveryRate?.toLocaleString() || "0",
      change: "",
      changeType: "positive",
      icon: Activity,
      iconBg: "bg-gray-50",
      iconColor: "text-gray-600",
    },
  ];

  useEffect(() => {
    if (!domainUrl) return;

    const fetchAnalytics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Dashboard analytics
        const analyticsResponse = await fetch(DASHBOARD_ANALYTICS_URL);
        if (!analyticsResponse.ok) throw new Error("Failed to fetch dashboard analytics");
        const analyticsResult = await analyticsResponse.json();
        if (analyticsResult.status !== 200) throw new Error(analyticsResult.error || "API error");

        setAnalyticsData({
          totalSubscribers: analyticsResult.data.totalSubscribers,
          totalNotification: analyticsResult.data.totalNotification,
          totalTopics: analyticsResult.data.totalTopics,
          totalScheduledNotification: analyticsResult.data.totalScheduledNotification,
          totalCtr: analyticsResult.data.totalCtr,
          totalOptIn: analyticsResult.data.totalOptIn,
          totalOptOut: analyticsResult.data.totalOptOut,
          deliveryRate: analyticsResult.data.deliveryRate,
        });

        // Device counts
        const deviceResponse = await fetch(DEVICE_COUNTS_URL);
        if (!deviceResponse.ok) throw new Error("Failed to fetch device counts");
        const deviceResult = await deviceResponse.json();
        if (deviceResult.status !== 200) throw new Error(deviceResult.error || "API error");

        const totalDeviceCount = deviceResult.data.reduce((sum, i) => sum + i.count, 0);
        const mappedDeviceData = deviceResult.data.map((i) => ({
          name: i.device.charAt(0).toUpperCase() + i.device.slice(1),
          value: i.count,
          percentage: totalDeviceCount ? ((i.count / totalDeviceCount) * 100).toFixed(1) : 0,
        }));

        const defaultDevices = [
          { name: "Mobile", value: 0, percentage: 0 },
          { name: "Desktop", value: 0, percentage: 0 },
          { name: "Other", value: 0, percentage: 0 },
        ];
        defaultDevices.forEach((def) => {
          const found = mappedDeviceData.find((d) => d.name.toLowerCase() === def.name.toLowerCase());
          if (found) {
            def.value = found.value;
            def.percentage = found.percentage;
          }
        });
        setDeviceData(defaultDevices);

        // Preference counts
        const preferenceResponse = await fetch(PREFERENCE_COUNTS_URL);
        if (!preferenceResponse.ok) throw new Error("Failed to fetch preference counts");
        const preferenceResult = await preferenceResponse.json();
        if (preferenceResult.status !== 200) throw new Error(preferenceResult.error || "API error");

        const totalPrefCount = preferenceResult.data.reduce((sum, i) => sum + i.count, 0);
        const mappedCampaignData = preferenceResult.data.map((i) => ({
          name: i.name.charAt(0).toUpperCase() + i.name.slice(1),
          value: i.count,
          percentage: totalPrefCount ? ((i.count / totalPrefCount) * 100).toFixed(1) : 0,
        }));
        setCampaignData(mappedCampaignData);

        // Unified daily records
        const dailyResponse = await fetch(DAILY_RECORD_URL);
        if (!dailyResponse.ok) throw new Error("Failed to fetch daily records");
        const dailyResult = await dailyResponse.json();
        if (dailyResult.status !== 200) throw new Error(dailyResult.error || "API error");

        // Fill missing dates with zeros
        const start = startDate || sevenDaysAgo;
        const end = endDate || today;
        const filledData = [];

        const apiMap = new Map();
        dailyResult.data.forEach((item) => {
          apiMap.set(item.recordDate, item);
        });

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateStr = format(d, "yyyy-MM-dd");
          const record = apiMap.get(dateStr) || {
            notificationCount: 0,
            newSubscriberCount: 0,
            unsubscriberCount: 0,
            clickCount: 0,
          };
          filledData.push({
            day: dateStr,
            notifications: record.notificationCount,
            created: record.newSubscriberCount,
            deleted: record.unsubscriberCount,
            clickCount: record.clickCount,
          });
        }

        setDailyRecords(filledData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [
    domainUrl,
    DASHBOARD_ANALYTICS_URL,
    DEVICE_COUNTS_URL,
    PREFERENCE_COUNTS_URL,
    DAILY_RECORD_URL,
    startDate,
    endDate,
  ]);

  return (
    <Layout bodynew={true}>
      <div className="flex min-h-screen w-full">
        <SidebarList />
        <main className="flex-1 overflow-auto">
          <div className="p-6 space-y-8">
            {/* Header & Date Picker */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Welcome back! Here's your analytics overview.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal w-[200px]",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : <span>Start date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                  </PopoverContent>
                </Popover>

                <span className="text-muted-foreground">to</span>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal w-[200px]",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : <span>End date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                  </PopoverContent>
                </Popover>

                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setStartDate(sevenDaysAgo);
                      setEndDate(today);
                    }}
                    className="h-9 px-2"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {isLoading ? (
                <div className="col-span-full flex justify-center items-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">Loading stats...</span>
                </div>
              ) : (
                stats.map((stat) => <StatCard key={stat.name} {...stat} />)
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Device & Category Pie Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Device Distribution */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold text-foreground mb-6">Subscribers by Device</h2>
                {isLoading ? (
                  <div className="flex justify-center items-center h-[300px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name} ${percentage}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                                <p className="font-semibold text-foreground">{data.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {data.value.toLocaleString()} users ({data.percentage}%)
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Category Selected */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold text-foreground mb-6">Category Selected</h2>
                {isLoading ? (
                  <div className="flex justify-center items-center h-[300px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={campaignData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {campaignData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                                <p className="font-semibold text-foreground">{data.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {data.value.toLocaleString()} ({data.percentage}%)
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Notifications Sent (Bar Chart) */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-xl font-bold text-foreground mb-6">
                Notifications Clicked
              </h2>
              {isLoading ? (
                <div className="flex justify-center items-center h-[300px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dailyRecords}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="clickCount"
                      name="Notifications Clicked"
                      fill="#8b5cf6"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-xl font-bold text-foreground mb-6">
                Notifications Sent
              </h2>
              {isLoading ? (
                <div className="flex justify-center items-center h-[300px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dailyRecords}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="notifications"
                      name="Notifications Sent"
                      fill="#8b5cf6"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Subscriber Activity (Opt-in / Opt-out) */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-xl font-bold text-foreground mb-6">
                Subscriber Activity (Opt-in / Opt-out)
              </h2>
              {isLoading ? (
                <div className="flex justify-center items-center h-[300px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyRecords}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      name="Opt-in"
                      dataKey="created"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: "#10b981", r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                    <Line
                      type="monotone"
                      name="Opt-out"
                      dataKey="deleted"
                      stroke="#ef4444"
                      strokeWidth={3}
                      dot={{ fill: "#ef4444", r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
}